/**
 * @copyright
 * Copyright(c) 2020 SICK AG
 */
import { createValueFormat } from './ValueFormat.js';
import { CssTools } from './CssTools.js';
import { resolveCssColor } from './ColorTools.js';
/**
 * Creates the font to be used for tooltips.
 *
 * INTERNAL USE ONLY
 *
 * @param fontFamily - the font-family to be used.
 * @param fontWeight - the font-weight to be used.
 */

export function createTooltipFont(fontFamily, fontWeight = 400) {
  return {
    family: fontFamily,
    weight: fontWeight,
    size: CssTools.REM2PIXEL(0.75),
    style: 'normal'
  };
}
/**
 * Creates the format to be used for the values shown by the tooltips.
 *
 * INTERNAL USE ONLY
 *
 * @param shorthandLabels - whether to use the short-hand notation
 * @param decimalPlaces - how many decimal places to be used
 */

export function createTooltipValueFormat(shorthandLabels, decimalPlaces) {
  const tooltipFormat = createValueFormat(shorthandLabels, decimalPlaces);
  return {
    format(value, locale) {
      return tooltipFormat(value, locale);
    }

  };
}
/**
 * Creates the style for a balloon tooltip with square symbols.
 *
 * INTERNAL USE ONLY
 */

export function createBalloonTooltipStyle(hostFontFamily, shorthandLabels, decimalPlaces) {
  const symbolSize = CssTools.REM2PIXEL(0.375);
  const tooltipStyle = {
    entriesGap: CssTools.REM2PIXEL(0.125),
    symbolSizes: [{
      width: symbolSize,
      height: symbolSize
    }],
    symbolLabelGap: CssTools.REM2PIXEL(0.25),
    tooltipFont: createTooltipFont(hostFontFamily, 600),
    headlineMarginBottom: CssTools.REM2PIXEL(0.25),
    headlineFont: createTooltipFont(hostFontFamily),
    tooltipFormat: createTooltipValueFormat(shorthandLabels, decimalPlaces)
  };
  const tooltipPadding = CssTools.REM2PIXEL(0.5);
  tooltipStyle.tooltipBoxStyle = {
    padding: {
      top: tooltipPadding,
      right: tooltipPadding,
      bottom: tooltipPadding,
      left: tooltipPadding
    },
    color: resolveCssColor('var(--davinci-color-text-light)'),
    backgroundColor: resolveCssColor('var(--davinci-color-background-input)'),
    borderColor: resolveCssColor('var(--davinci-color-outline-light)'),
    borderRadius: {
      topLeft: 4,
      topRight: 4,
      bottomRight: 4,
      bottomLeft: 4
    },
    shadow: {
      color: 'rgba(0, 0, 0, 0.25)',
      offsetX: 5,
      offsetY: 5,
      blurRadius: 10
    }
  };
  return tooltipStyle;
}
/**
 * Creates the box-styles to be used for the tooltips (for decimal values).
 *
 * INTERNAL USE ONLY
 *
 * @param dataSeriesColors - the colors to be used for the tooltips
 */

export function createTooltipDecimalsBoxStyles(dataSeriesColors) {
  if (dataSeriesColors.length === 0) {
    return [];
  }

  const tooltipTextColor = resolveCssColor('var(--davinci-color-background)');
  const tooltipBorderColor = tooltipTextColor;
  const tooltipPadding = createTooltipPadding();
  const tooltipBorderRadius = createTooltipBorderRadius();
  const tooltipShadow = createTooltipShadow();
  return dataSeriesColors.map(color => {
    return {
      padding: tooltipPadding,
      color: tooltipTextColor,
      backgroundColor: color,
      borderColor: tooltipBorderColor,
      borderRadius: tooltipBorderRadius,
      shadow: tooltipShadow
    };
  });
}
/**
 * Creates the box-style to be used for the tooltips (for enum values).
 * The background color is not set - we are using the (context aware) defaults
 *
 * INTERNAL USE ONLY
 */

export function createTooltipEnumsBoxStyles() {
  const tooltipTextColor = resolveCssColor('var(--davinci-color-background)');
  return {
    padding: createTooltipPadding(),
    color: tooltipTextColor,
    backgroundColor: null,
    // use the context fill
    borderColor: tooltipTextColor,
    borderRadius: createTooltipBorderRadius(),
    shadow: createTooltipShadow()
  };
}
/**
 * Creates the padding used for tooltips.
 *
 * INTERNAL USE ONLY
 */

export function createTooltipPadding() {
  const horizontalTooltipPadding = CssTools.REM2PIXEL(0.5);
  const verticalTooltipPadding = CssTools.REM2PIXEL(0.25);
  return {
    top: verticalTooltipPadding,
    right: horizontalTooltipPadding,
    bottom: verticalTooltipPadding,
    left: horizontalTooltipPadding
  };
}
/**
 * Creates the shadow used for tooltips.
 *
 * INTERNAL USE ONLY
 */

export function createTooltipShadow() {
  return {
    color: 'rgba(0, 0, 0, 0.25)',
    offsetX: 2,
    offsetY: 2,
    blurRadius: 5
  };
}
/**
 * Creates the border-radius used for tooltips.
 *
 * INTERNAL USE ONLY
 */

export function createTooltipBorderRadius() {
  return {
    topLeft: 3,
    topRight: 3,
    bottomRight: 3,
    bottomLeft: 3
  };
}
